import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ScreenableFieldSetupContext } from '../helpers/useScreenableFieldSetupContextManager';
import { SalvButton, SalvDropdown, SalvFieldHeader, Size } from '@salvapp/ui-components';
import { faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useQueryEntitySchema } from '../../../schema/entitySchemaActions';
import { InternalApiEntityType } from '../../../api-internal';
import { isChildField } from '../../../schema/helper';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import InternalLink from '../../../common/InternalLink';

export type GoodlistIdentifiers = {
  goodlistKey1?: string;
  goodlistKey2?: string;
  goodlistKey3?: string;
};

export const GoodlistingStep = () => {
  const { goodlistIdentifiers, updateGoodlistIdentifiers } = useContext(ScreenableFieldSetupContext);
  const [showGoodlistFields, setShowGoodlistFields] = useState<boolean>(
    Object.values(goodlistIdentifiers).some((it) => !!it)
  );
  const { data: transactionSchema } = useQueryEntitySchema(InternalApiEntityType.TRANSACTION);
  const transactionSchemaFields = transactionSchema?.schemaFields.filter(
    (schemaField) => !isChildField(schemaField) && !schemaField.isMulti
  );

  const options = useMemo(() => {
    return (
      transactionSchemaFields?.map((field) => ({
        key: field.fieldName,
        value: field.fieldName,
        label: field.displayName,
      })) ?? []
    );
  }, [transactionSchemaFields]);

  const removeGoodlisting = useCallback(() => {
    setShowGoodlistFields(false);
    updateGoodlistIdentifiers({});
  }, [updateGoodlistIdentifiers]);

  const changeGoodlistKey = useCallback(
    (option: DropdownOption, goodlistKey: keyof GoodlistIdentifiers) => {
      if (!option) {
        updateGoodlistIdentifiers({ ...goodlistIdentifiers, [goodlistKey]: undefined });
      } else {
        updateGoodlistIdentifiers({ ...goodlistIdentifiers, [goodlistKey]: option.key });
      }
    },
    [goodlistIdentifiers, updateGoodlistIdentifiers]
  );

  return (
    <div className='p-a-24'>
      <div>
        <h4 className='text-strong-m chapter-title'>Goodlist Identifiers (optional)</h4>
        <span className='text-s chapter-subtitle m-t-4'>
          Salv remembers matches that have been closed as false positives and automatically goodlists them to avoid
          duplicate alerts, using an entity’s goodlist identifiers + matches for the goodlist record. Goodlist
          identifiers are defined as a set of data fields that act as unique identifiers for an entity. Learn more about
          Goodlisting{' '}
          <InternalLink
            title={'here'}
            link={
              'https://help.salv.com/en/articles/147838-goodlisting-and-significant-update-indicators-in-transaction-screening'
            }
          />
        </span>
        <div className='m-t-24'>
          {showGoodlistFields ? (
            <div className='w-25'>
              <SalvFieldHeader
                label='Goodlisting keys'
                description={'Choose up to 3 Goodlisting keys'}
                size={Size.SMALL}
              />
              <SalvDropdown
                className='m-b-12'
                placeholder='Choose keys...'
                ariaLabel='goodlistKey1'
                options={options.filter(
                  (it) => it.key !== goodlistIdentifiers.goodlistKey2 && it.key !== goodlistIdentifiers.goodlistKey3
                )}
                value={options.find((it) => it.key === goodlistIdentifiers.goodlistKey1)}
                onChange={([option]) => changeGoodlistKey(option, 'goodlistKey1')}
              />
              <SalvDropdown
                className='m-b-12'
                placeholder='Choose keys...'
                ariaLabel='goodlistKey2'
                options={options.filter(
                  (it) => it.key !== goodlistIdentifiers.goodlistKey1 && it.key !== goodlistIdentifiers.goodlistKey3
                )}
                value={options.find((it) => it.key === goodlistIdentifiers.goodlistKey2)}
                onChange={([option]) => changeGoodlistKey(option, 'goodlistKey2')}
              />
              <SalvDropdown
                className='m-b-24'
                placeholder='Choose keys...'
                ariaLabel='goodlistKey3'
                options={options.filter(
                  (it) => it.key !== goodlistIdentifiers.goodlistKey1 && it.key !== goodlistIdentifiers.goodlistKey2
                )}
                value={options.find((it) => it.key === goodlistIdentifiers.goodlistKey3)}
                onChange={([option]) => changeGoodlistKey(option, 'goodlistKey3')}
              />
              <SalvButton
                variant='muted'
                label='Remove Goodlisting'
                size={Size.SMALL}
                iconLeft={faXmark}
                onClick={() => removeGoodlisting()}
              />
            </div>
          ) : (
            <SalvButton
              variant='primary'
              label='Add Goodlisting'
              size={Size.SMALL}
              iconLeft={faPlus}
              onClick={() => setShowGoodlistFields(true)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
